'use client'

import { Container } from '@/components/ui/container'
import { cn, normalizeVietnamese } from '@/lib/utils'
import { useSettingData } from '@/providers/setting-provider'
import Link from 'next/link'
import { useEffect, useState } from 'react'

export function TopBarPromotion({
  isScrollingDown,
}: {
  isScrollingDown: boolean
}) {
  const homeSetting = useSettingData()
  const [currentIndex, setCurrentIndex] = useState(0)

  const items = homeSetting?.topbar?.items || []

  useEffect(() => {
    if (items.length <= 1) return

    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev + 1)
    }, 5000)

    return () => clearInterval(interval)
  }, [items.length])

  if (
    homeSetting?.topbar?.hide ||
    !homeSetting?.topbar?.items ||
    items.length === 0
  ) {
    return null
  }

  const currentItem = items[currentIndex % items.length]

  return (
    <div
      aria-hidden={isScrollingDown}
      className={cn(
        'min-h-[34px] items-center overflow-hidden max-md:px-4 lg:h-[34px]',
        !homeSetting?.topbar?.background && 'bg-neutral-600',
        !homeSetting?.topbar?.textColor && 'text-light'
      )}
      style={{
        display: isScrollingDown ? 'none' : 'flex',
        transform: isScrollingDown ? 'translateY(-100%)' : 'translateY(0)',
        transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
        backgroundColor: homeSetting?.topbar?.background || undefined,
        color: homeSetting?.topbar?.textColor || undefined,
      }}
    >
      <Container size='full' className='relative py-1 lg:h-full'>
        <div className='relative overflow-hidden lg:h-full lg:overflow-y-hidden'>
          {/* Hidden element on mobile to measure content height */}
          <div
            aria-hidden='true'
            className='invisible flex items-center justify-center text-xs font-medium uppercase text-inherit lg:hidden lg:text-sm lg:leading-4.5'
          >
            <span className='text-center'>
              <span className='inline'>
                {items[currentIndex % items.length]?.heading}
              </span>
              <span className='ml-4 inline leading-4.5 text-inherit underline underline-offset-1'>
                {items[currentIndex % items.length]?.cta_text}
              </span>
            </span>
          </div>

          {/* Actual items with absolute positioning for animation */}
          {items.map((item, index) => {
            // Use modulo to create infinite loop effect
            const actualIndex = currentIndex % items.length
            const offset = index - actualIndex
            // Only show current item to prevent next item from showing when wrapping
            const isVisible = offset === 0

            return (
              <a
                href={item.link ?? '/'}
                aria-label={item.cta_text}
                className=''
                ga-tracking-value={`topbar-promotion__${normalizeVietnamese(
                  item.cta_text?.toLowerCase().replace(' ', '-') || ''
                )}`}
                ga-tracking-label={item.cta_text}
                key={`${index}-${Math.floor(currentIndex / items.length)}`}
              >
                <div
                  className='absolute left-0 right-0 flex h-full items-center justify-center text-xs text-inherit max-md:top-0 lg:whitespace-nowrap lg:text-sm lg:leading-4.5'
                  style={{
                    transform: `translateY(${offset * 100}%)`,
                    opacity: isVisible ? 1 : 0,
                    visibility: isVisible ? 'visible' : 'hidden',
                    pointerEvents: offset === 0 ? 'auto' : 'none',
                    transition:
                      'transform 0.5s ease-in-out, opacity 0.5s ease-in-out',
                  }}
                >
                  <span className='text-center max-md:inline lg:block'>
                    <span
                      className='inline'
                      dangerouslySetInnerHTML={{ __html: item.heading }}
                    ></span>
                    <span className='ml-4 inline leading-4.5 text-inherit underline underline-offset-1 max-md:hidden'>
                      {item.cta_text}
                    </span>
                  </span>
                </div>
              </a>
            )
          })}
        </div>
      </Container>
      <Link href={currentItem?.link ?? '/'} className='ml-1 md:hidden'>
        →
      </Link>
    </div>
  )
}
